import {
  createGrid,
  ModuleRegistry,
  ClientSideRowModelModule,
  CsvExportModule,
  CommunityFeaturesModule,
} from 'ag-grid-community';
import type { ColDef, GridOptions, GridApi } from 'ag-grid-community';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  CommunityFeaturesModule,
]);

export interface DataGridConfig<T> {
  container: HTMLElement;
  rowData: T[];
  columnDefs: ColDef<T>[];
  groupBy?: string[];
  exportFilename?: string;
  quickFilterPlaceholder?: string;
}

export function mountGrid<T>(cfg: DataGridConfig<T>): GridApi<T> {
  cfg.container.innerHTML = `
    <div class="h-full flex flex-col gap-3 p-4">
      <div class="flex items-center gap-3">
        <input
          id="quick-filter"
          type="search"
          placeholder="${cfg.quickFilterPlaceholder ?? 'Buscar en toda la tabla…'}"
          class="flex-1 max-w-md px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-pulse-400 focus:border-transparent"
        />
        <span class="text-xs text-slate-500" id="row-count"></span>
        <div class="ml-auto flex gap-2">
          <button id="btn-clear" class="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100">Limpiar filtros</button>
          <button id="btn-export" class="text-xs px-3 py-1.5 rounded-md border border-pulse-300 bg-pulse-50 text-pulse-700 hover:bg-pulse-100">Exportar CSV</button>
        </div>
      </div>
      <div id="grid" class="flex-1 ag-theme-quartz rounded-lg overflow-hidden border border-slate-200"></div>
    </div>
  `;

  const gridDiv = cfg.container.querySelector('#grid') as HTMLDivElement;
  const opts: GridOptions<T> = {
    columnDefs: cfg.columnDefs,
    rowData: cfg.rowData,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      minWidth: 100,
    },
    animateRows: true,
    suppressRowClickSelection: true,
    rowGroupPanelShow: 'always',
    sideBar: {
      toolPanels: [
        { id: 'columns', labelDefault: 'Columnas', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
        { id: 'filters', labelDefault: 'Filtros', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
      ],
      defaultToolPanel: '',
    },
    onModelUpdated: (ev) => {
      const rc = cfg.container.querySelector('#row-count') as HTMLElement;
      const count = ev.api.getDisplayedRowCount();
      const total = cfg.rowData.length;
      rc.textContent = count === total ? `${total} filas` : `${count} de ${total} filas`;
    },
  };

  const api = createGrid(gridDiv, opts);

  const quickFilter = cfg.container.querySelector('#quick-filter') as HTMLInputElement;
  quickFilter.addEventListener('input', () => {
    api.setGridOption('quickFilterText', quickFilter.value);
  });

  (cfg.container.querySelector('#btn-clear') as HTMLButtonElement).addEventListener('click', () => {
    api.setFilterModel(null);
    quickFilter.value = '';
    api.setGridOption('quickFilterText', '');
  });

  (cfg.container.querySelector('#btn-export') as HTMLButtonElement).addEventListener('click', () => {
    api.exportDataAsCsv({
      fileName: cfg.exportFilename ?? 'pulse-export.csv',
      columnSeparator: ';',
    });
  });

  return api;
}
